import { useState, useRef, useCallback, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Upload, Save, X, FolderOpen, Move, RotateCcw } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useBackgroundImage } from '@/hooks/useBackgroundImage';

interface BackgroundImageUploaderProps {
  onImageSaved?: (imageUrl: string) => void;
  page?: string;
}

export const BackgroundImageUploader = ({ onImageSaved, page = 'home' }: BackgroundImageUploaderProps) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [backgroundPosition, setBackgroundPosition] = useState('center center');
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const previewRef = useRef<HTMLDivElement>(null);
  const { toast } = useToast();
  const { backgroundImage, isLoading } = useBackgroundImage(page, false);

  // Load existing background image on mount
  useEffect(() => {
    if (backgroundImage && !selectedFile) {
      setImageUrl(backgroundImage.image_url);
      setBackgroundPosition(backgroundImage.background_position);
    }
  }, [backgroundImage, selectedFile]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast({
        title: "Invalid file type",
        description: "Please select an image file",
        variant: "destructive"
      });
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast({
        title: "File too large",
        description: "Please select an image smaller than 10MB",
        variant: "destructive"
      });
      return;
    }

    setSelectedFile(file);
    
    // Create preview URL
    const url = URL.createObjectURL(file);
    setImageUrl(url);
    // Reset position when new image is selected
    setBackgroundPosition('center center');
  };

  const handleBrowse = () => {
    fileInputRef.current?.click();
  };

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
    setDragStart({ x: e.clientX, y: e.clientY });
  }, []);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isDragging) return;
    
    const deltaX = e.clientX - dragStart.x;
    const deltaY = e.clientY - dragStart.y;
    
    // Convert pixels to percentage for smooth positioning
    const rect = e.currentTarget.getBoundingClientRect();
    const xPercent = (deltaX / rect.width) * 100;
    const yPercent = (deltaY / rect.height) * 100;
    
    // Parse current position and adjust
    const [currentX, currentY] = parseBackgroundPosition(backgroundPosition);
    const newX = Math.max(0, Math.min(100, currentX + xPercent));
    const newY = Math.max(0, Math.min(100, currentY + yPercent));
    
    setBackgroundPosition(`${newX.toFixed(1)}% ${newY.toFixed(1)}%`);
    setDragStart({ x: e.clientX, y: e.clientY });
  }, [isDragging, dragStart, backgroundPosition]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const parseBackgroundPosition = (position: string): [number, number] => {
    if (position === 'center center') return [50, 50];
    if (position === 'left top') return [0, 0];
    if (position === 'right bottom') return [100, 100];
    
    const parts = position.split(' ');
    const x = parseFloat(parts[0]) || 50;
    const y = parseFloat(parts[1]) || 50;
    return [x, y];
  };

  const setPresetPosition = (position: string) => {
    setBackgroundPosition(position);
  };


  const handleSaveImage = async (asDraft: boolean = true) => {
    if (!selectedFile || !imageUrl) return;

    setIsUploading(true);
    try {
      // Convert file to base64 for storage
      const reader = new FileReader();
      reader.onload = async (e) => {
        const base64 = e.target?.result as string;
        
        if (!asDraft) {
          // If publishing, deactivate current published images and archive them
          await supabase
            .from('background_images')
            .update({ is_active: false, status: 'archived' })
            .eq('is_active', true)
            .eq('page', page)
            .eq('status', 'published');
        }

        // Save new background image to database with position and status
        const { error } = await supabase
          .from('background_images')
          .insert({
            image_url: base64,
            background_position: backgroundPosition,
            is_active: true,
            page: page,
            status: asDraft ? 'draft' : 'published'
          });

        if (error) throw error;

        toast({
          title: "Success",
          description: asDraft 
            ? "Background image saved as draft" 
            : "Background image published successfully"
        });

        onImageSaved?.(base64);
        
        // Reset to default view after saving
        handleClear();
      };
      
      reader.readAsDataURL(selectedFile);
      } catch (error) {
        // Errors are logged in development only via secureLog
        toast({
        title: "Error",
        description: "Failed to save background image",
        variant: "destructive"
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleClear = () => {
    setSelectedFile(null);
    setImageUrl(null);
    setBackgroundPosition('center center');
    setIsDragging(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Move className="h-5 w-5" />
          Background Image Editor - {page.charAt(0).toUpperCase() + page.slice(1)} Page
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-3">
          <Label>Select Background Image</Label>
          <div className="flex gap-2">
            <Input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              className="hidden"
            />
            <Button 
              onClick={handleBrowse}
              variant="outline"
              className="flex items-center gap-2"
            >
              <FolderOpen className="h-4 w-4" />
              Browse Files
            </Button>
            {selectedFile && (
              <span className="text-sm text-muted-foreground py-2">
                {selectedFile.name}
              </span>
            )}
          </div>
        </div>

        {imageUrl && (
          <div className="space-y-6">
            {/* Position Presets */}
            <div className="space-y-3">
              <Label>Quick Position Presets</Label>
              <div className="grid grid-cols-3 gap-2">
                <Button variant="outline" size="sm" onClick={() => setPresetPosition('left top')}>
                  Top Left
                </Button>
                <Button variant="outline" size="sm" onClick={() => setPresetPosition('center top')}>
                  Top Center
                </Button>
                <Button variant="outline" size="sm" onClick={() => setPresetPosition('right top')}>
                  Top Right
                </Button>
                <Button variant="outline" size="sm" onClick={() => setPresetPosition('left center')}>
                  Center Left
                </Button>
                <Button variant="outline" size="sm" onClick={() => setPresetPosition('center center')}>
                  <RotateCcw className="h-3 w-3 mr-1" />
                  Center
                </Button>
                <Button variant="outline" size="sm" onClick={() => setPresetPosition('right center')}>
                  Center Right
                </Button>
                <Button variant="outline" size="sm" onClick={() => setPresetPosition('left bottom')}>
                  Bottom Left
                </Button>
                <Button variant="outline" size="sm" onClick={() => setPresetPosition('center bottom')}>
                  Bottom Center
                </Button>
                <Button variant="outline" size="sm" onClick={() => setPresetPosition('right bottom')}>
                  Bottom Right
                </Button>
              </div>
            </div>

            {/* Multi-Viewport Previews */}
            <div className="space-y-4">
              <Label>Live Preview - Multiple Screen Sizes</Label>
              <div className="grid md:grid-cols-3 gap-4">
                {/* Mobile Preview */}
              <div className="space-y-2">
                <div className="text-xs font-normal text-center">Mobile (375x667)</div>
                  <div 
                    className="relative mx-auto border border-muted rounded-lg overflow-hidden shadow-md"
                    style={{ width: '187px', height: '333px' }}
                  >
                    <div 
                      className="absolute inset-0"
                      style={{ 
                        backgroundImage: `url(${imageUrl})`,
                        backgroundSize: 'cover',
                        backgroundPosition: backgroundPosition,
                        backgroundRepeat: 'no-repeat',
                        backgroundAttachment: 'scroll'
                      }}
                    />
                    {/* Mobile UI Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/40" />
                    <div className="absolute top-2 left-2 right-2 bg-white/90 backdrop-blur-sm rounded p-2">
                      <div className="text-xs font-normal">Mobile View</div>
                    </div>
                  </div>
                </div>

                {/* Tablet Preview */}
              <div className="space-y-2">
                <div className="text-xs font-normal text-center">Tablet (768x1024)</div>
                  <div 
                    className="relative mx-auto border border-muted rounded-lg overflow-hidden shadow-md"
                    style={{ width: '192px', height: '256px' }}
                  >
                    <div 
                      className="absolute inset-0"
                      style={{ 
                        backgroundImage: `url(${imageUrl})`,
                        backgroundSize: 'cover',
                        backgroundPosition: backgroundPosition,
                        backgroundRepeat: 'no-repeat',
                        backgroundAttachment: 'scroll'
                      }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/40" />
                    <div className="absolute top-2 left-2 right-2 bg-white/90 backdrop-blur-sm rounded p-2">
                      <div className="text-xs font-normal">Tablet View</div>
                    </div>
                  </div>
                </div>

                {/* Desktop Preview */}
              <div className="space-y-2">
                <div className="text-xs font-normal text-center">Desktop (1920x1080)</div>
                  <div 
                    className="relative mx-auto border border-muted rounded-lg overflow-hidden shadow-md"
                    style={{ width: '256px', height: '144px' }}
                  >
                    <div 
                      className="absolute inset-0"
                      style={{ 
                        backgroundImage: `url(${imageUrl})`,
                        backgroundSize: 'cover',
                        backgroundPosition: backgroundPosition,
                        backgroundRepeat: 'no-repeat',
                        backgroundAttachment: 'scroll'
                      }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/40" />
                    <div className="absolute top-2 left-2 right-2 bg-white/90 backdrop-blur-sm rounded p-2">
                      <div className="text-xs font-normal">Desktop View</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Interactive Drag Preview */}
            <div className="space-y-3">
              <Label>Interactive Editor</Label>
              <div 
                ref={previewRef}
                className="relative mx-auto border-2 border-primary rounded-lg overflow-hidden shadow-lg"
                style={{ 
                  width: '100%', 
                  maxWidth: '600px',
                  height: '400px'
                }}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
              >
                <div 
                  className={`absolute inset-0 ${isDragging ? 'cursor-grabbing' : 'cursor-grab'}`}
                  style={{ 
                    backgroundImage: `url(${imageUrl})`,
                    backgroundSize: 'cover',
                    backgroundPosition: backgroundPosition,
                    backgroundRepeat: 'no-repeat'
                  }}
                />
                
                {/* Grid Lines Overlay */}
                <div className="absolute inset-0 pointer-events-none opacity-30">
                  {[...Array(3)].map((_, i) => (
                    <div
                      key={`v-${i}`}
                      className="absolute top-0 bottom-0 border-l border-white"
                      style={{ left: `${(i + 1) * 25}%` }}
                    />
                  ))}
                  {[...Array(3)].map((_, i) => (
                    <div
                      key={`h-${i}`}
                      className="absolute left-0 right-0 border-t border-white"
                      style={{ top: `${(i + 1) * 25}%` }}
                    />
                  ))}
                </div>
                
                {/* Position indicator */}
                <div className="absolute top-2 right-2 bg-black/70 text-white px-2 py-1 rounded text-xs pointer-events-none">
                  Position: {backgroundPosition}
                </div>
                
                {/* Instructions */}
                <div className="absolute bottom-2 left-2 bg-black/70 text-white px-2 py-1 rounded text-xs pointer-events-none">
                  {isDragging ? 'Dragging...' : 'Click and drag to reposition image'}
                </div>
              </div>
            </div>
            
            {/* Preview Info */}
            <div className="text-center text-sm text-muted-foreground">
              <p>Preview shows how your background will look across different devices</p>
              <p>Drag the image in the editor to reposition • Use presets for quick positioning</p>
            </div>

            <div className="flex gap-2">
              <Button 
                onClick={() => handleSaveImage(true)} 
                disabled={isUploading}
                variant="outline"
                className="flex-1"
              >
                <Save className="h-4 w-4 mr-2" />
                {isUploading ? 'Saving...' : 'Save as Draft'}
              </Button>
              <Button 
                onClick={() => handleSaveImage(false)} 
                disabled={isUploading}
                className="flex-1"
              >
                <Upload className="h-4 w-4 mr-2" />
                {isUploading ? 'Publishing...' : 'Publish Live'}
              </Button>
              <Button 
                onClick={handleClear} 
                variant="outline"
              >
                <X className="h-4 w-4 mr-2" />
                Clear
              </Button>
            </div>
          </div>
        )}

        {!imageUrl && (
          <div className="text-center py-12 text-muted-foreground border-2 border-dashed border-muted-foreground/30 rounded-lg">
            <Upload className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p className="text-lg font-normal mb-2">No image selected</p>
            <p className="text-sm">Click "Browse Files" to select a background image</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};